import { Box, Button, Flex, Heading, IconButton, Link, Skeleton, useDisclosure } from '@chakra-ui/react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'react-use';

import BookmarkIcon from '@/assets/icons/ic-bookmark.svg';
import CopyIcon from '@/assets/icons/ic-copy.svg';
import QuoteIcon from '@/assets/icons/ic-quote.svg';
import ShareIcon from '@/assets/icons/ic-share.svg';

import { BookmarkLectureModal } from '@/components/ui/features/lecture/BookmarkLectureModal/BookmarkLectureModal';
import { CiteLectureModal } from '@/components/ui/features/lecture/CiteLectureModal/CiteLectureModal';
import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { getLectureVariant } from '@/components/ui/features/lecture/LectureHeaderSection/LectureHeaderSection.utils';
import { AuthorsList } from '@/components/ui/features/lecture/LectureInfo/components/AuthorsList';
import { EmbedButton } from '@/components/ui/features/lecture/LectureInfo/components/EmbedButton';
import { LectureInfoSubtitle } from '@/components/ui/features/lecture/LectureInfo/components/LectureInfoSubtitle';
import { LabelText } from '@/components/ui/features/lecture/LectureInfo/LectureInfo.elements';
import { ShareLectureModal } from '@/components/ui/features/lecture/ShareLectureModal/ShareLectureModal';
import { LogInPopup } from '@/components/ui/shared/modals/LoginPopup/LogInPopup';
import { useAnalytics } from '@/context/Analytics';
import { LectureVariants } from '@/enums/LectureVariants';
import { IToastType } from '@/enums/ToastType';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import useSession from '@/hooks/useSession';
import { useToast } from '@/hooks/useToast';
import { useResource } from '@/lib/datx-jsonapi-react';

const DOI_BASE_URL = 'https://doi.org/';

export const LectureInfo: FC = () => {
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { user } = useSession();
  const [clipboardState, copyToClipboard] = useCopyToClipboard();
  const toast = useToast();

  const { id: lectureId } = useLecturePageQueryParams();

  const { isOpen: isCiteOpen, onOpen: onCiteOpen, onClose: onCiteClose } = useDisclosure();
  const { isOpen: isShareOpen, onOpen: onShareOpen, onClose: onShareClose } = useDisclosure();
  const { isOpen: isBookmarkOpen, onOpen: onBookmarkOpen, onClose: onBookmarkClose } = useDisclosure();
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();

  const { data: lecture } = useResource(queryThinLectureWithTagAndEvent(lectureId));
  const lectureVariant = useMemo(() => getLectureVariant(lecture), [lecture]);

  const showEmbedButton = lectureVariant === LectureVariants.Full || lectureVariant === LectureVariants.Video;

  const shareOpenHandler = () => {
    onShareOpen();
    analytics.track('library_lecture_share', {
      event_id: lecture.eventId,
      lecture_name: lecture.title,
      speaker_name: lecture.sortedProfiles.map((speaker) => speaker.fullName),
      lecture_id: lecture.id,
    });
  };

  const citeOpenHandler = () => {
    onCiteOpen();
    analytics.track('library_lecture_cite', {
      event_id: lecture.eventId,
      lecture_name: lecture.title,
      speaker_name: lecture.sortedProfiles.map((speaker) => speaker.fullName),
      lecture_id: lecture.id,
    });
  };

  function handleOpenBookmark() {
    if (user) {
      onBookmarkOpen();
      return;
    }
    onLoginOpen();
  }

  const onVideoDoiCopy = () => {
    copyToClipboard(DOI_BASE_URL + lecture?.underlineDoi);

    if (clipboardState.error) {
      toast({ status: IToastType.ERROR, title: t('newLecture.copyError') });
    } else {
      toast({ status: IToastType.SUCCESS, title: t('newLecture.copySuccess') });
    }
  };

  return (
    <Flex justifyContent="space-between" flexDirection="column-reverse" color="white">
      <Flex gap="4" flexDirection={{ base: 'column', lg: 'row' }} mt="4">
        {lecture?.underlineDoi && (
          <Box>
            {t('newLecture.underlineDoi')}: {DOI_BASE_URL + lecture?.underlineDoi}
            <IconButton
              ml="2"
              w="6"
              h="6"
              onClick={onVideoDoiCopy}
              aria-label="copy doi"
              variant="unstyled"
              icon={<CopyIcon width="24px" h="24px" />}
            />
          </Box>
        )}
        {lecture?.doi && (
          <Box>
            {t('newLecture.paperDoi')}:{' '}
            <Link target="_blank" href={lecture?.doi}>
              {lecture?.doi}
            </Link>
          </Box>
        )}
      </Flex>
      <AuthorsList />
      <Box mt={4}>
        <Flex mb={3} flexDirection={['column', null, null, 'row']}>
          {/* TODO: Move to LectureInfoSubtitle */}
          {lecture?.tag?.name ? (
            <LabelText textTransform="uppercase" mr={6}>
              {lecture?.tag?.name}
            </LabelText>
          ) : (
            <Skeleton height="1.2rem" width="150px" mr={6} />
          )}
          {<LectureInfoSubtitle />}
        </Flex>
        <Box>
          {!lecture ? (
            <Skeleton height="1.6rem" width="300px" mt={4} />
          ) : (
            <Heading fontSize={['2xl', null, '3xl', '4xl']} lineHeight={['2rem', null, '2.5rem', '3rem']}>
              {lecture?.title}
            </Heading>
          )}
        </Box>
      </Box>
      {lecture ? (
        <Box
          sx={{
            flexShrink: '0',
            '& svg': {
              width: '24px',
              marginLeft: '5px',
            },
          }}
        >
          <Button type="button" variant="ghost" colorScheme="white" onClick={handleOpenBookmark} pl={0}>
            {t('newLecture.bookmark')}
            <BookmarkIcon />
          </Button>
          <Button type="button" variant="ghost" colorScheme="white" onClick={shareOpenHandler} pl={0}>
            {t('newLecture.share')}
            <ShareIcon />
          </Button>
          <Button type="button" variant="ghost" colorScheme="white" onClick={citeOpenHandler} pl={0}>
            {t('newLecture.cite')}
            <QuoteIcon />
          </Button>
          {showEmbedButton && <EmbedButton />}
        </Box>
      ) : (
        <Flex mt="4">
          <Skeleton height="1.6rem" width="150px" mr={4} />
          <Skeleton height="1.6rem" width="150px" />
        </Flex>
      )}
      <CiteLectureModal lectureId={lectureId} isOpen={isCiteOpen} onClose={onCiteClose} />
      <ShareLectureModal lectureId={lectureId} isOpen={isShareOpen} onClose={onShareClose} />
      <BookmarkLectureModal lectureId={lectureId} isOpen={isBookmarkOpen} onClose={onBookmarkClose} />
      <LogInPopup isOpen={isLoginOpen} onClose={onLoginClose} />
    </Flex>
  );
};
